import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, Clipboard, X } from "lucide-react";
import { PARSE_TYPES, ACCEPT, PASTE_PLACEHOLDER } from "./smartUploadModel";

const modeCls = (active) => (active ? "bg-ism-teal/20 text-ism-teal border-ism-teal/30" : "border-gray-700 text-gray-400");

export const ModeSelector = ({ mode, setMode }) => (
  <div className="flex gap-2 mb-4">
    <Button variant={mode === "file" ? "default" : "outline"} onClick={() => setMode("file")} className={modeCls(mode === "file")} data-testid="smart-upload-mode-file">
      <Upload className="h-4 w-4 mr-2" /> Upload File
    </Button>
    <Button variant={mode === "paste" ? "default" : "outline"} onClick={() => setMode("paste")} className={modeCls(mode === "paste")} data-testid="smart-upload-mode-paste">
      <Clipboard className="h-4 w-4 mr-2" /> Paste Text
    </Button>
  </div>
);

export const ParseTypeGrid = ({ parseType, setParseType }) => (
  <div className="mb-4">
    <label className="text-sm text-gray-400 mb-2 block">What type of data is this?</label>
    <div className="grid grid-cols-3 gap-2">
      {PARSE_TYPES.map((type) => {
        const Icon = type.icon;
        return (
          <button key={type.id} onClick={() => setParseType(type.id)} data-testid={`smart-upload-type-${type.id}`}
            className={`p-3 rounded-lg border text-left transition-all ${parseType === type.id ? "bg-ism-teal/10 border-ism-teal/30" : "bg-white/5 border-gray-800 hover:border-gray-600"}`}>
            <div className="flex items-center gap-2 mb-1"><Icon className={`h-4 w-4 ${type.color}`} /><span className="text-sm text-white">{type.label}</span></div>
            <p className="text-xs text-gray-500">{type.description}</p>
          </button>
        );
      })}
    </div>
  </div>
);

const FileDrop = ({ file, setFile }) => {
  const inputRef = useRef(null);
  return (
    <div onClick={() => inputRef.current?.click()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }} onDragOver={(e) => e.preventDefault()}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${file ? "border-ism-teal/50 bg-ism-teal/5" : "border-gray-700 hover:border-gray-500"}`} data-testid="smart-upload-dropzone">
      <input ref={inputRef} type="file" onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }} className="hidden" accept={ACCEPT} data-testid="smart-upload-file-input" />
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <FileText className="h-8 w-8 text-ism-teal" />
          <div className="text-left">
            <p className="text-white font-normal">{file.name}</p>
            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="p-1 hover:bg-white/10 rounded" aria-label="Remove file"><X className="h-4 w-4 text-gray-500" /></button>
        </div>
      ) : (
        <>
          <Upload className="h-10 w-10 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400 mb-1">Drop a file here or click to browse</p>
          <p className="text-xs text-gray-600">Supports: PDF, Word, Text, CSV, Excel</p>
        </>
      )}
    </div>
  );
};

const fade = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -10 } };

export const InputArea = ({ mode, file, setFile, pastedText, setPastedText }) => (
  <AnimatePresence mode="wait">
    {mode === "file" ? (
      <motion.div key="file" {...fade}><FileDrop file={file} setFile={setFile} /></motion.div>
    ) : (
      <motion.div key="paste" {...fade}>
        <Textarea value={pastedText} onChange={(e) => setPastedText(e.target.value)} placeholder={PASTE_PLACEHOLDER} className="min-h-[180px] bg-white/5 border-gray-700 text-white resize-none" data-testid="smart-upload-text" />
      </motion.div>
    )}
  </AnimatePresence>
);
